/// <reference types="../assets" />
import { css, styled } from 'next-yak';

import { mediaConfined, mediaWide, mediaXWide } from '@xing-com/layout-tokens';
import { createPolymorphicElement } from '@xing-com/polymorphic-element';
import {
  cornerRadiusL,
  cornerRadiusM,
  scale005,
  scale010,
  scale110,
  scale130,
  scale160,
  scale190,
  scale220,
  scale250,
  scale280,
  scale310,
  scale340,
  spaceXXS,
  xdlColorBackgroundTertiary,
  xdlColorFocusRing,
} from '@xing-com/tokens';

import maskImage from '../assets/super-ellipse-mask.svg';

import type { SuperEllipseSize } from './super-ellipse.types';

type SuperEllipseTransientProps = {
  $size?: SuperEllipseSize;
  $sizeConfined?: SuperEllipseSize;
  $sizeWide?: SuperEllipseSize;
  $sizeXWide?: SuperEllipseSize;
};

type ImageContainerTransientProps = {
  $blur?: boolean;
  $blurValue?: string;
};

// *** Sizes ***
export const sizes = {
  xxsmall: css`
    height: ${scale110};
    width: ${scale110};

    & .strokeWidth {
      stroke-width: ${scale005};
    }
  `,
  xsmall: css`
    height: ${scale130};
    width: ${scale130};

    & .strokeWidth {
      stroke-width: ${scale005};
    }
  `,
  small: css`
    height: ${scale160};
    width: ${scale160};

    & .strokeWidth {
      stroke-width: ${scale005};
    }
  `,
  medium: css`
    height: ${scale190};
    width: ${scale190};

    & .strokeWidth {
      stroke-width: ${scale005};
    }
  `,
  large: css`
    height: ${scale220};
    width: ${scale220};

    & .strokeWidth {
      stroke-width: ${scale010};
    }
  `,
  xlarge: css`
    height: ${scale250};
    width: ${scale250};

    & .strokeWidth {
      stroke-width: ${scale010};
    }
  `,
  xxlarge: css`
    height: ${scale280};
    width: ${scale280};

    & .strokeWidth {
      stroke-width: ${scale010};
    }
  `,
  xxxlarge: css`
    height: ${scale310};
    width: ${scale310};

    & .strokeWidth {
      stroke-width: ${scale010};
    }
  `,
  xxxxlarge: css`
    height: ${scale340};
    width: ${scale340};

    & .strokeWidth {
      stroke-width: ${scale010};
    }
  `,
};

// *** Components ***
const SuperEllipseElement = createPolymorphicElement('div');

export const SuperEllipse = styled(
  SuperEllipseElement
)<SuperEllipseTransientProps>`
  ${({ $size }) => {
    switch ($size) {
      case 'xxsmall':
        return sizes.xxsmall;
      case 'xsmall':
        return sizes.xsmall;
      case 'small':
        return sizes.small;
      case 'medium':
        return sizes.medium;
      case 'large':
        return sizes.large;
      case 'xlarge':
        return sizes.xlarge;
      case 'xxlarge':
        return sizes.xxlarge;
      case 'xxxlarge':
        return sizes.xxxlarge;
      case 'xxxxlarge':
        return sizes.xxxxlarge;
      default:
        return $size satisfies undefined;
    }
  }};

  position: relative;
  border-radius: ${cornerRadiusL};

  & .strokeWidth {
    stroke: ${xdlColorBackgroundTertiary};
  }

  &:focus {
    outline: ${scale010} solid ${xdlColorFocusRing};
    outline-offset: ${spaceXXS};
  }

  &:focus:not(:focus-visible) {
    outline: none;
  }

  &:focus-visible {
    outline: ${scale010} solid ${xdlColorFocusRing};
    outline-offset: ${spaceXXS};
  }

  ${({ $sizeConfined }) => {
    switch ($sizeConfined) {
      case 'xxsmall':
        return css`
          @media ${mediaConfined} {
            ${sizes.xxsmall}
          }
        `;
      case 'xsmall':
        return css`
          @media ${mediaConfined} {
            ${sizes.xsmall}
          }
        `;
      case 'small':
        return css`
          @media ${mediaConfined} {
            ${sizes.small}
          }
        `;
      case 'medium':
        return css`
          @media ${mediaConfined} {
            ${sizes.medium}
          }
        `;
      case 'large':
        return css`
          @media ${mediaConfined} {
            ${sizes.large}
          }
        `;
      case 'xlarge':
        return css`
          @media ${mediaConfined} {
            ${sizes.xlarge}
          }
        `;
      case 'xxlarge':
        return css`
          @media ${mediaConfined} {
            ${sizes.xxlarge}
          }
        `;
      case 'xxxlarge':
        return css`
          @media ${mediaConfined} {
            ${sizes.xxxlarge}
          }
        `;
      case 'xxxxlarge':
        return css`
          @media ${mediaConfined} {
            ${sizes.xxxxlarge}
          }
        `;
      default:
        return $sizeConfined satisfies undefined;
    }
  }};

  ${({ $sizeWide }) => {
    switch ($sizeWide) {
      case 'xxsmall':
        return css`
          @media ${mediaWide} {
            ${sizes.xxsmall}
          }
        `;
      case 'xsmall':
        return css`
          @media ${mediaWide} {
            ${sizes.xsmall}
          }
        `;
      case 'small':
        return css`
          @media ${mediaWide} {
            ${sizes.small}
          }
        `;
      case 'medium':
        return css`
          @media ${mediaWide} {
            ${sizes.medium}
          }
        `;
      case 'large':
        return css`
          @media ${mediaWide} {
            ${sizes.large}
          }
        `;
      case 'xlarge':
        return css`
          @media ${mediaWide} {
            ${sizes.xlarge}
          }
        `;
      case 'xxlarge':
        return css`
          @media ${mediaWide} {
            ${sizes.xxlarge}
          }
        `;
      case 'xxxlarge':
        return css`
          @media ${mediaWide} {
            ${sizes.xxxlarge}
          }
        `;
      case 'xxxxlarge':
        return css`
          @media ${mediaWide} {
            ${sizes.xxxxlarge}
          }
        `;
      default:
        return $sizeWide satisfies undefined;
    }
  }};

  ${({ $sizeXWide }) => {
    switch ($sizeXWide) {
      case 'xxsmall':
        return css`
          @media ${mediaXWide} {
            ${sizes.xxsmall}
          }
        `;
      case 'xsmall':
        return css`
          @media ${mediaXWide} {
            ${sizes.xsmall}
          }
        `;
      case 'small':
        return css`
          @media ${mediaXWide} {
            ${sizes.small}
          }
        `;
      case 'medium':
        return css`
          @media ${mediaXWide} {
            ${sizes.medium}
          }
        `;
      case 'large':
        return css`
          @media ${mediaXWide} {
            ${sizes.large}
          }
        `;
      case 'xlarge':
        return css`
          @media ${mediaXWide} {
            ${sizes.xlarge}
          }
        `;
      case 'xxlarge':
        return css`
          @media ${mediaXWide} {
            ${sizes.xxlarge}
          }
        `;
      case 'xxxlarge':
        return css`
          @media ${mediaXWide} {
            ${sizes.xxxlarge}
          }
        `;
      case 'xxxxlarge':
        return css`
          @media ${mediaXWide} {
            ${sizes.xxxxlarge}
          }
        `;
      default:
        return $sizeXWide satisfies undefined;
    }
  }};
`;

export const ImageContainer = styled.div<ImageContainerTransientProps>`
  display: block;
  height: 100%;
  mask-image: ${() => `url(${maskImage})`};
  mask-position: center;
  mask-repeat: no-repeat;
  position: absolute;
  width: 100%;

  ${({ $blur, $blurValue }) =>
    $blur && $blurValue
      ? css`
          filter: blur(${() => $blurValue});
          pointer-events: none;
        `
      : null};
`;

export const Image = styled.img`
  aspect-ratio: 1 / 1;
  background-color: ${xdlColorBackgroundTertiary};
  border-radius: ${cornerRadiusM};
  display: block;
  height: 100%;
  margin: 0;
  padding: 0;
  position: relative;
  width: 100%;
`;

export const ImageSvgBorder = styled.svg`
  height: 100%;
  inset-block-end: 0;
  inset-block-start: 0;
  inset-inline-end: 0;
  inset-inline-start: 0;
  margin: auto;
  padding: 0;
  pointer-events: none;
  position: relative;
  width: 100%;
`;
